﻿import { IEditor } from '../interface/editor';
import { AsyncValidatorFn, ValidatorFn } from '../types/validator-fn';
import { ValidatorError } from '../types/validator-error';
import { IForm } from '../interface/form';

export abstract class BaseEditor<ValueType> implements IEditor {
    private _errorContainer: HTMLElement;
    private _formInputContiner: HTMLDivElement;
    id ='';
    isValid = false;
    isPending = false;
    isValidated = false;
    disabled = false;
    valueChanged?: (value: ValueType | null) => void;

    constructor(
        public form: IForm,
        public name: string,
        public element: HTMLElement,
        public validators: ValidatorFn[],
        public asyncValidators: AsyncValidatorFn[]) {
        this.id = this.name.replace('.', '_');

        const errContainer = element.querySelector<HTMLElement>('.t-error');
        const inputContainer = element.querySelector<HTMLDivElement>('div.form-input');
        if(!errContainer || !inputContainer) {
            throw new Error(`Cannot find .t-error & div.form-input in field ${name}`);
        }

        this._errorContainer =errContainer!;
        this._formInputContiner = inputContainer!;

        const initDisabledValue = this.getAttributeValue('disabled');
        if (initDisabledValue) {
            this.disabled = initDisabledValue === '1';
        }
    }

    abstract get value(): ValueType | null;
    abstract set value(value: ValueType | null);
    abstract setFocus():void;

    abstract setDisabled(disabled: boolean): void;
    setDisabledInternal(disabled: boolean): void {
        this.setAttributeValue('disabled', disabled ? '1' : '0');
        this.disabled = disabled;
    }

    setError(errors: ValidatorError[] | null): void {
        this._errorContainer.innerHTML = "";
        if (errors && errors.length>0) {
            errors.forEach(er => {
                const errorEle = document.createElement('div');
                errorEle.innerText = er.message;
                this._errorContainer.appendChild(errorEle);
            });
            this._errorContainer.classList.remove('d-none');
            this._errorContainer.classList.add('invalid-tooltip');
            this._formInputContiner.classList.add('is-invalid');
        } else {
            this._errorContainer.classList.remove('invalid-tooltip');
            this._errorContainer.classList.add('d-none');
            this._formInputContiner.classList.remove('is-invalid');
        }
    }

    validate():void {
        if (this.isValidated) {
            //do not do validate again
            return;
        }
        this.isValid = false;
        this.isPending = true;
        const validateErrors: ValidatorError[] = [];
        if (this.validators && this.validators.length > 0) {
            this.validators.forEach(v => {
                const error = v(this);
                if (error) {
                    validateErrors.push(error);
                }
            });
        }
        this.setError(validateErrors);
        if (validateErrors.length === 0 && this.asyncValidators && this.asyncValidators.length > 0) {
            //if donot have error, then do async validate
            this.isPending = true;
            const validateCount = this.asyncValidators.length;
            let successCount = 0;
            for (let i = 0; i < this.asyncValidators.length; i++) {
                this.asyncValidators[i](this).subscribe(validateResult => {
                    if (validateResult) {
                        validateErrors.push(validateResult);
                    }
                    successCount++;
                    if (successCount === validateCount) {
                        this.setError(validateErrors);
                        this.isValid = validateErrors.length === 0;
                        this.isPending = false;
                        this.isValidated = true;
                    }
                });
            }
        }
        else {
            this.isValid = validateErrors.length === 0;
            this.isPending = false;
            this.isValidated = true;
        }
    }

    reValidate():void {
        this.isValidated = false;
        this.validate();
    }

    getAttributeValue(attributeName: string): string | null {
        return this.element.getAttribute(attributeName);
    }
    setAttributeValue(attributeName: string, value: string):void {
        this.element.setAttribute(attributeName, value);
    }

    notifyChanges(value: ValueType | null):void {
        if (typeof (this.valueChanged) === 'function') {
            this.valueChanged(value);
        }
    }
}